import { useEffect, useState } from "react";
import api from "../api";
import toast from "react-hot-toast";

export default function UsersPage() {
  const [users, setUsers] = useState([]);

  // =====================
  // CRÉATION
  // =====================

  const [nom, setNom] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("receptionniste");

  // =====================
  // ÉDITION
  // =====================

  const [editingUser, setEditingUser] = useState(null);
  const [editNom, setEditNom] = useState("");
  const [editRole, setEditRole] = useState("");

  // =====================
  // MOT DE PASSE
  // =====================

  const [editPassword, setEditPassword] = useState("");
  const [confirmEditPassword, setConfirmEditPassword] =
    useState("");

  // =====================
  // LOADING
  // =====================

  const [loadingCreate, setLoadingCreate] =
    useState(false);

  const [loadingUpdate, setLoadingUpdate] =
    useState(false);

  // =====================
  // RÔLES
  // =====================

  const roleLabels = {
    admin: "Admin",
    comptable: "Comptable",
    receptionniste: "Réception",
    prestataire: "Prestataire",
  };

  const roles = [
    {
      value: "admin",
      label: "Admin",
    },
    {
      value: "comptable",
      label: "Comptable",
    },
    {
      value: "receptionniste",
      label: "Réception",
    },
    {
      value: "prestataire",
      label: "Prestataire",
    },
  ];

  // =====================
  // VALIDATION MOT DE PASSE
  // =====================

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

  // =====================
  // FETCH
  // =====================

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");

      setUsers(res.data);
    } catch (err) {
      console.error(
        "Erreur chargement utilisateurs:",
        err
      );

      toast.error(
        err.response?.data?.error ||
          "Impossible de charger les utilisateurs"
      );
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // =====================
  // CREATE
  // =====================

  const createUser = async () => {
    if (!nom.trim() || !password || !role) {
      toast.error(
        "Nom, mot de passe et rôle requis"
      );

      return;
    }

    if (!passwordRegex.test(password)) {
      toast.error(
        "Mot de passe faible : 8 caractères minimum, avec majuscule, minuscule et chiffre"
      );

      return;
    }

    setLoadingCreate(true);

    const toastId =
      toast.loading(
        "Création en cours..."
      );

    try {
      await api.post("/users", {
        nom: nom.trim(),
        mot_de_passe: password,
        role,
      });

      setNom("");
      setPassword("");
      setRole("receptionniste");

      await fetchUsers();

      toast.success(
        "Utilisateur créé",
        { id: toastId }
      );
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          "Erreur lors de la création",
        { id: toastId }
      );
    } finally {
      setLoadingCreate(false);
    }
  };

  // =====================
  // EDIT
  // =====================

  const openEdit = (user) => {
    setEditingUser(user);

    setEditNom(user.nom || "");
    setEditRole(user.role || "");

    // Toujours vide à l'ouverture.
    // On ne récupère évidemment jamais
    // le mot de passe existant.
    setEditPassword("");
    setConfirmEditPassword("");
  };

  // =====================
  // FERMER MODAL
  // =====================

  const closeEdit = () => {
    if (loadingUpdate) return;

    setEditingUser(null);

    setEditNom("");
    setEditRole("");
    setEditPassword("");
    setConfirmEditPassword("");
  };

  // =====================
  // UPDATE
  // =====================

  const updateUser = async () => {
    if (
      !editNom.trim() ||
      !editRole
    ) {
      toast.error("Champs requis");

      return;
    }

    // =====================
    // UTILISATEUR CONNECTÉ
    // =====================

    let currentUser = null;

    try {
      const rawUser =
        localStorage.getItem("user");

      currentUser = rawUser
        ? JSON.parse(rawUser)
        : null;
    } catch {
      currentUser = null;
    }

    // =====================
    // PROTECTION ADMIN
    // =====================

    if (
      editingUser.id === currentUser?.id &&
      editRole !== "admin"
    ) {
      toast.error(
        "Impossible de retirer votre rôle admin"
      );

      return;
    }

    // =====================
    // MOT DE PASSE
    // =====================

    const changingPassword =
      editPassword.trim() !== "" ||
      confirmEditPassword.trim() !== "";

    if (changingPassword) {
      if (!passwordRegex.test(editPassword)) {
        toast.error(
          "Nouveau mot de passe faible : 8 caractères minimum, avec majuscule, minuscule et chiffre"
        );

        return;
      }

      if (
        editPassword !==
        confirmEditPassword
      ) {
        toast.error(
          "Les mots de passe ne correspondent pas"
        );

        return;
      }
    }

    // =====================
    // CONFIRMATION
    // =====================

    const confirmUpdate =
      window.confirm(
        changingPassword
          ? "Confirmer la modification de l'utilisateur et du mot de passe ?"
          : "Confirmer la modification ?"
      );

    if (!confirmUpdate) return;

    setLoadingUpdate(true);

    const toastId =
      toast.loading(
        "Modification en cours..."
      );

    try {
      // =====================
      // MODIFIER NOM + RÔLE
      // =====================

      await api.put(
        `/users/${editingUser.id}`,
        {
          nom: editNom.trim(),
          role: editRole,
        }
      );

      // =====================
      // MODIFIER MOT DE PASSE
      // =====================

      if (changingPassword) {
        await api.put(
          `/users/${editingUser.id}/password`,
          {
            mot_de_passe: editPassword,
          }
        );
      }

      await fetchUsers();

      toast.success(
        changingPassword
          ? "Utilisateur et mot de passe modifiés"
          : "Utilisateur modifié",
        { id: toastId }
      );

      closeEdit();
    } catch (err) {
      console.error(
        "Erreur modification utilisateur:",
        err
      );

      toast.error(
        err.response?.data?.error ||
          "Erreur lors de la modification",
        { id: toastId }
      );
    } finally {
      setLoadingUpdate(false);
    }
  };

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto space-y-4 md:space-y-6 min-h-[100dvh]">

      {/* =====================
          HEADER
      ===================== */}

      <h1 className="text-2xl md:text-3xl font-bold">
        Utilisateurs
      </h1>

      {/* =====================
          CRÉATION
      ===================== */}

      <div className="bg-white p-3 md:p-4 rounded-2xl shadow space-y-3">

        <input
          placeholder="Nom"
          value={nom}
          onChange={(e) =>
            setNom(e.target.value)
          }
          className="w-full border p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-400"
        />

        <input
          placeholder="Mot de passe"
          type="password"
          value={password}
          onChange={(e) =>
            setPassword(e.target.value)
          }
          className="w-full border p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-400"
        />

        <p className="text-xs text-gray-500">
          8 caractères minimum, avec au moins
          une majuscule, une minuscule et un chiffre.
        </p>

        <select
          value={role}
          onChange={(e) =>
            setRole(e.target.value)
          }
          className="w-full border p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-400"
        >
          {roles.map((item) => (
            <option
              key={item.value}
              value={item.value}
            >
              {item.label}
            </option>
          ))}
        </select>

        <button
          onClick={createUser}
          disabled={loadingCreate}
          className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
        >
          {loadingCreate
            ? "Création..."
            : "Créer utilisateur"}
        </button>

      </div>

      {/* =====================
          LISTE
      ===================== */}

      <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

        <div className="overflow-x-auto">

          <table className="min-w-[650px] w-full text-sm">

            <thead>
              <tr className="border-b">
                <th className="text-left p-3">
                  Nom
                </th>

                <th className="text-left p-3">
                  Rôle
                </th>

                <th className="text-left p-3">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>

              {users.map((u) => (

                <tr
                  key={u.id}
                  className="border-b hover:bg-gray-50 transition"
                >

                  <td className="p-3 font-medium whitespace-nowrap">
                    {u.nom}
                  </td>

                  <td className="p-3 whitespace-nowrap">
                    {roleLabels[u.role] ||
                      u.role}
                  </td>

                  <td className="p-3">

                    <button
                      onClick={() =>
                        openEdit(u)
                      }
                      className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 transition whitespace-nowrap"
                    >
                      Modifier
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* =====================
          MODAL MODIFICATION
      ===================== */}

      {editingUser && (

        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">

          <div className="bg-white p-4 md:p-6 rounded-2xl w-full max-w-sm space-y-4 shadow-xl">

            <div>
              <h2 className="text-lg md:text-xl font-bold">
                Modifier utilisateur
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                {editingUser.nom}
              </p>
            </div>

            {/* NOM */}

            <div className="space-y-1">

              <label className="text-sm font-semibold text-gray-700">
                Nom
              </label>

              <input
                value={editNom}
                onChange={(e) =>
                  setEditNom(
                    e.target.value
                  )
                }
                className="w-full border p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
              />

            </div>

            {/* RÔLE */}

            <div className="space-y-1">

              <label className="text-sm font-semibold text-gray-700">
                Rôle
              </label>

              <select
                value={editRole}
                onChange={(e) =>
                  setEditRole(
                    e.target.value
                  )
                }
                className="w-full border p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
              >

                {roles.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </option>
                ))}

              </select>

            </div>

            {/* MOT DE PASSE */}

            <div className="border-t pt-4 space-y-3">

              <div>
                <h3 className="font-semibold">
                  Modifier le mot de passe
                </h3>

                <p className="text-xs text-gray-500 mt-1">
                  Laisser vide pour conserver le
                  mot de passe actuel.
                </p>
              </div>

              <input
                type="password"
                placeholder="Nouveau mot de passe"
                value={editPassword}
                onChange={(e) =>
                  setEditPassword(
                    e.target.value
                  )
                }
                className="w-full border p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
              />

              <input
                type="password"
                placeholder="Confirmer le mot de passe"
                value={confirmEditPassword}
                onChange={(e) =>
                  setConfirmEditPassword(
                    e.target.value
                  )
                }
                className="w-full border p-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
              />

              <p className="text-xs text-gray-500">
                8 caractères minimum, avec au moins
                une majuscule, une minuscule et un chiffre.
              </p>

            </div>

            {/* ACTIONS */}

            <div className="flex flex-col md:flex-row gap-2 md:justify-between">

              <button
                onClick={closeEdit}
                disabled={loadingUpdate}
                className="px-4 py-3 bg-gray-400 text-white rounded-xl hover:bg-gray-500 transition disabled:opacity-50"
              >
                Annuler
              </button>

              <button
                onClick={updateUser}
                disabled={loadingUpdate}
                className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loadingUpdate
                  ? "Enregistrement..."
                  : "Enregistrer"}
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}